import { createContext, useContext, useState, useEffect, useCallback } from "react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }) {
  const [account, setAccount] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const r = await fetch(`${API}/auth/me`, { credentials: "include" });
      setAccount(r.ok ? await r.json() : null);
    } catch {
      setAccount(null);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const logout = useCallback(async () => {
    try {
      await fetch(`${API}/auth/logout`, { method: "POST", credentials: "include" });
    } catch {
      /* no-op */
    }
    setAccount(null);
  }, []);

  // Phase 5D — update the authenticated account's name on the canonical record.
  const updateName = useCallback(async (name) => {
    const r = await fetch(`${API}/auth/name`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ name }),
    });
    if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || "Could not save name");
    const d = await r.json();
    if (d.account) setAccount(d.account);
    return d.account;
  }, []);

  // Phase 5E — request a one-time account-deletion code (sent to the account's
  // verified email; destination derived server-side from the session).
  const requestDeletionCode = useCallback(async () => {
    const r = await fetch(`${API}/account/deletion/request-code`, {
      method: "POST",
      credentials: "include",
    });
    if (!r.ok) {
      const d = await r.json().catch(() => ({}));
      throw new Error(d.detail || "Could not send verification code");
    }
    return true;
  }, []);

  // Phase 5D/5E — permanent account deletion. account_id is derived server-side from
  // the session; the server enforces the typed "DELETE" confirmation AND the deletion OTP.
  const deleteAccount = useCallback(async (code) => {
    const r = await fetch(`${API}/account/delete`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ confirmation: "DELETE", code }),
    });
    if (!r.ok) {
      const d = await r.json().catch(() => ({}));
      throw new Error(d.detail || "Account deletion could not be completed");
    }
    setAccount(null);
    return true;
  }, []);

  // Phase 5B — after authentication, claim the single just-completed anonymous attempt
  // (if a short-lived claim token was stashed in sessionStorage before the auth transition).
  const claimPending = useCallback(async () => {
    const token = sessionStorage.getItem("adaptience_claim");
    if (!token) return null;
    let result = null;
    try {
      const r = await fetch(`${API}/auth/claim-session`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ claim_token: token }),
      });
      if (r.ok) result = await r.json();
    } catch {
      /* no-op */
    }
    sessionStorage.removeItem("adaptience_claim");
    return result;
  }, []);

  return (
    <AuthContext.Provider value={{ account, loading, refresh, logout, setAccount, claimPending, updateName, requestDeletionCode, deleteAccount }}>
      {children}
    </AuthContext.Provider>
  );
}

export default AuthProvider;
